import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import QueryParams from 'utils/queryParams';
import { showResult } from 'infrastructure/hooks/utils';

import { useCallsDue } from './use-calls-due';

import type {
  TOverviewPatientProps,
  TStatusCard,
  TStatusNote,
} from 'components/molecules/oneplaform/overview-patients/types';

export type TOverviewPatientSummaryResponse = {
  Critical: number;
  AtRisk: number;
  Normal: number;
  PatientsWorkWith: number;
};

export const HOME_OVERVIEW_QUERY_KEY = ['home-dashboard', 'overview-patients'];

export type TOverviewPatientSummaryFilters = {
  startTime?: number;
  endTime?: number;
};

const fetchOverviewPatientSummary = async (
  _filters?: TOverviewPatientSummaryFilters,
): Promise<TOverviewPatientSummaryResponse> => {
  const endpoint = QueryParams.stringifyUrl(
    'reports/reports/interactive-time-dashboard-overview-count',
    {
      reviewed: false,
      latestReadingStartTime: _filters?.startTime,
      latestReadingEndTime: _filters?.endTime,
    },
  );

  const { data, error } = await dataService.getList<{
    critical?: number;
    atRisk?: number;
    normal?: number;
  }>(endpoint);

  if (error || !data) {
    const message = error || 'Unable to load patient overview';
    showResult(message);
    throw new Error(message);
  }

  return {
    Critical: data.critical ?? 0,
    AtRisk: data.atRisk ?? 0,
    Normal: data.normal ?? 0,
    PatientsWorkWith: 6,
  };
};

export const useOverviewPatientSummary = (
  roleNames: string[] = [],
  filters?: TOverviewPatientSummaryFilters,
) => {
  const callsDue = useCallsDue();

  const {
    data: overviewSummary,
    isLoading,
    isFetching,
    error,
  } = useQuery<TOverviewPatientSummaryResponse, Error>({
    queryKey: [
      ...HOME_OVERVIEW_QUERY_KEY,
      filters?.startTime ?? null,
      filters?.endTime ?? null,
    ],
    queryFn: () => fetchOverviewPatientSummary(filters),
  });

  const statusCards: TStatusCard[] = useMemo(() => {
    if (!overviewSummary) return [];

    return [
      {
        id: 'critical',
        label: 'Critical',
        value: overviewSummary.Critical ?? 0,
      },
      {
        id: 'at-risk',
        label: 'At-Risk',
        value: overviewSummary.AtRisk ?? 0,
      },
      {
        id: 'normal',
        label: 'Normal',
        value: overviewSummary.Normal ?? 0,
      },
    ];
  }, [overviewSummary]);

  const statusNotes: TStatusNote[] = useMemo(() => {
    if (!overviewSummary) return [];

    return [
      {
        id: 'calls-due',
        label: 'Calls Due',
        value: callsDue.count,
        details: callsDue.details,
        onItemClick: callsDue.onItemClick,
      },
    ];
  }, [overviewSummary, callsDue]);

  const overviewPatientsProps: TOverviewPatientProps = useMemo(
    () => ({
      roleNames,
      loading: isLoading || isFetching || callsDue.isLoading,
      statusCards,
      statusNotes,
      emptyStateText:
        error || callsDue.error ? 'Unable to load patient overview' : undefined,
    }),
    [
      roleNames,
      isLoading,
      isFetching,
      callsDue.isLoading,
      statusCards,
      statusNotes,
      error,
    ],
  );

  return { overviewPatientsProps };
};
